import { FC, useEffect, useState, useRef, useCallback, MouseEvent } from 'react';
import { Button, IconButton, Typography, Popover } from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Layout } from '@orientaction/components';
import { getFromLS } from '@orientaction/utils';
import { ReactComponent as CalendarTodayIcon } from '../../Asset/svg/calendar.svg';
import StatsTable from '../../components/StatsTable';
import { getChatbotReviews, getChatbotStatistics } from '../../services/ai.service';
import { getUserById } from '../../services/user.service';
import { useStyles } from './style';
import { DateRangePicker } from 'react-date-range';
import { fr } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const getDateKey = (d: Date | null): number | null => d?.getTime() ?? null;

const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const Statistics: FC = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<any[]>([
    {
      startDate: null,
      endDate: null,
      key: 'selection',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [reviewsData, setReviewsData] = useState<any[]>([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [totalReviewsCount, setTotalReviewsCount] = useState(0);
  const [averageTime, setAverageTime] = useState<number>(0);
  const [averageRating, setAverageRating] = useState<number>(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRange = useRef(false);

  const fetchStatisticsData = useCallback(
    async (currentPage: number) => {
      setLoading(true);
      try {
        const token = getFromLS('userToken');
        if (!token) {
          setReviewsData([]);
          setTotalReviews(0);
          setAverageTime(0);
          setAverageRating(0);
          setLoading(false);
          return;
        }

        const { startDate, endDate } = dateRange[0];
        const startDateStr = startDate ? toLocalDateString(startDate) : undefined;
        const endDateStr = endDate ? toLocalDateString(endDate) : undefined;

        const stats = await getChatbotStatistics(startDateStr, endDateStr);
        setTotalReviews(stats.totalChatbots);
        setAverageTime(stats.averageInteractionTimeMinutes);
        setAverageRating(stats.averageRating);

        const response = await getChatbotReviews(currentPage, 10, startDateStr, endDateStr);
        setTotalReviewsCount(response.meta.total);

        const usersPromises = response.data.map((review: any) =>
          getUserById({ token, userId: String(review.strapiUserId) })
            .then((userResponse: any) => {
              const user = userResponse?.data || userResponse;
              return { review, user: user || null };
            })
            .catch(() => ({ review, user: null }))
        );

        const usersData = await Promise.all(usersPromises);

        const formattedData = usersData.map(({ review, user }: any) => {
          const serviceName = user?.service?.name || '-';
          return {
            id: review.id,
            name: user
              ? `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'N/A'
              : 'Utilisateur inconnu',
            email: user?.email || 'N/A',
            prestation: serviceName,
            date: new Date(review.chatbotCreatedAt).toLocaleDateString('fr-FR'),
            note: review.note,
            commentaire: review.comment,
          };
        });

        setReviewsData(formattedData);
      } catch (error) {
        setReviewsData([]);
        setTotalReviews(0);
        setAverageTime(0);
        setAverageRating(0);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getDateKey(dateRange[0].startDate), getDateKey(dateRange[0].endDate)]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(
      () => {
        fetchStatisticsData(page);
      },
      isSelectingRange.current ? 600 : 0
    );

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [page, fetchStatisticsData]);

  const handleOpenDatePicker = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDatePicker = () => {
    setAnchorEl(null);
    isSelectingRange.current = false;
  };

  const handleDateRangeChange = (item: any) => {
    const { startDate, endDate } = item.selection;
    const rangeComplete = startDate && endDate && startDate.getTime() !== endDate.getTime();

    if (!rangeComplete) {
      isSelectingRange.current = true;
    } else {
      isSelectingRange.current = false;
    }

    setDateRange([item.selection]);
    setPage(0);
    setExpandedRow(null);
  };

  const handleShownDateChange = (date: Date) => {
    isSelectingRange.current = false;
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    setDateRange([
      {
        startDate: startOfMonth,
        endDate: endOfMonth,
        key: 'selection',
      },
    ]);
    setPage(0);
    setExpandedRow(null);
  };

  const handleClearDates = () => {
    isSelectingRange.current = false;
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: 'selection',
      },
    ]);
    setPage(0);
    setExpandedRow(null);
    handleCloseDatePicker();
  };

  const formatDateRangeDisplay = () => {
    const { startDate, endDate } = dateRange[0];
    if (!startDate && !endDate) return 'Filtrer par période';
    if (startDate && endDate) {
      const sameMonth =
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear();
      const isFullMonth =
        sameMonth &&
        startDate.getDate() === 1 &&
        endDate.getDate() === new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();

      if (isFullMonth) {
        return startDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      }
      return `Du ${startDate.toLocaleDateString('fr-FR')} au ${endDate.toLocaleDateString(
        'fr-FR'
      )}`;
    }
    if (startDate) return `Du ${startDate.toLocaleDateString('fr-FR')}`;
    if (endDate) return `Jusqu'au ${endDate.toLocaleDateString('fr-FR')}`;
    return 'Filtrer par période';
  };

  const renderStars = (rating: number) => {
    return (
      <div className={classes.starsContainer}>
        {Array.from({ length: 5 }, (_, index) => {
          const starNumber = index + 1;
          const isFilled = starNumber <= rating;
          return (
            <svg
              key={starNumber}
              width="20"
              height="20"
              viewBox="0 0 15 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: 2 }}>
              <path
                d="M7.50065 0.333984L9.56065 4.50732L14.1673 5.18065L10.834 8.42732L11.6207 13.014L7.50065 10.8473L3.38065 13.014L4.16732 8.42732L0.833984 5.18065L5.44065 4.50732L7.50065 0.333984Z"
                fill={isFilled ? '#FACC15' : '#D4D4D8'}
              />
            </svg>
          );
        })}
      </div>
    );
  };

  const toggleRowExpansion = (index: number) => {
    if (expandedRow === index) {
      // If clicking on the already expanded row, close it
      setExpandedRow(null);
    } else {
      // Otherwise, close any open row and open the clicked one
      setExpandedRow(index);
    }
  };

  // Column definitions for StatsTable
  const columns = [
    {
      id: 'utilisateur',
      label: 'UTILISATEUR',
      minWidth: 200,
      format: (value: any) => (
        <div>
          <div className={classes.userName}>{value.name}</div>
          <div className={classes.userEmail}>{value.email}</div>
        </div>
      ),
    },
    {
      id: 'prestation',
      label: 'PRESTATIONS',
      minWidth: 150,
    },
    {
      id: 'date',
      label: 'DATE',
      minWidth: 120,
    },
    {
      id: 'note',
      label: "NOTE DE L'UTILISATEUR",
      minWidth: 150,
      align: 'center' as const,
      format: (value: number) => renderStars(value),
    },
    {
      id: 'commentaire',
      label: 'COMMENTAIRE',
      minWidth: 300,
      format: (value: { text: string; index: number }) => (
        <div className={classes.commentaireContainer}>
          <div className={classes.commentaireText}>
            {value.text.length > 75
              ? expandedRow === value.index
                ? value.text
                : `${value.text.slice(0, 75)}...`
              : value.text}
          </div>
          {value.text.length > 75 && (
            <IconButton
              size="small"
              onClick={() => toggleRowExpansion(value.index)}
              className={classes.expandButton}>
              {expandedRow === value.index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </div>
      ),
    },
  ];
  // Transform data for StatsTable
  const transformedData = reviewsData.map((item, index) => ({
    utilisateur: {
      name: item.name,
      email: item.email,
    },
    prestation: item.prestation,
    date: item.date,
    note: item.note,
    commentaire: {
      text: item.commentaire,
      index: index + page * 10,
    },
  }));

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setExpandedRow(null);
  };

  return (
    <Layout isaddBackground={true}>
      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <Typography className={classes.title}>Statistiques IA</Typography>
          <div className={classes.datePickerContainer}>
            <Button
              className={classes.datePickerButton}
              onClick={handleOpenDatePicker}
              startIcon={<CalendarTodayIcon />}
              endIcon={<ExpandMoreIcon />}>
              {formatDateRangeDisplay()}
            </Button>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseDatePicker}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}>
              <div className={classes.dateRangePickerWrapper}>
                <DateRangePicker
                  onChange={handleDateRangeChange}
                  onShownDateChange={handleShownDateChange}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  ranges={dateRange}
                  direction="horizontal"
                  locale={fr}
                  dateDisplayFormat="dd/MM/yyyy"
                />
                {(dateRange[0].startDate || dateRange[0].endDate) && (
                  <Button
                    onClick={handleClearDates}
                    className={classes.clearButton}
                    size="small"
                    fullWidth={true}>
                    Effacer
                  </Button>
                )}
              </div>
            </Popover>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={classes.statsContainer}>
          <div className={classes.statCard}>
            <Typography className={classes.statNumber}>{totalReviews}</Typography>
            <Typography className={classes.statLabel}>Nb. de requête IA</Typography>
          </div>
          <div className={classes.statCard}>
            <Typography className={classes.statNumber}>
              {loading ? '-' : averageTime > 0 ? averageTime : '-'}
            </Typography>
            <Typography className={classes.statLabel}>
              Durée moyenne d&apos;interaction (min)
            </Typography>
          </div>
          <div className={classes.statCard}>
            <Typography className={classes.statNumber}>
              {loading ? '-' : averageRating > 0 ? averageRating.toFixed(1) : '-'}
            </Typography>
            <Typography className={classes.statLabel}>Note moyenne (sur 5)</Typography>
          </div>
        </div>

        {/* Comments Section */}
        <div>
          <Typography className={classes.sectionTitle}>Liste des commentaires</Typography>

          <StatsTable
            columns={columns}
            data={transformedData}
            loading={loading}
            bgColor="#23296F"
            pagination={{
              count: totalReviewsCount,
              onChangePage: handleChangePage,
              page,
              rowsPerPage: 10,
            }}
          />
        </div>
      </div>
    </Layout>
  );
};

export default Statistics;
